import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaServer, FaHeart, FaCode, FaBrain } from 'react-icons/fa';
import { ServicesLinks } from '../data/servicesData';
import BCCLogo from '../assets/images/bcc_logo.png';
import BLogo from '../assets/images/bcc_b_logo.png';
import RavirajPhoto from '../assets/images/raviraj_shetty.jpeg';

/* ── Data ────────────────────────────────────────────────────────── */

const stats = [
  { value: '5+', label: 'Years Experience' },
  { value: '10+', label: 'Projects Shipped' },
  { value: '100%', label: 'Client Satisfaction' },
  { value: '100%', label: 'On-Time Delivery' },
];

const values = [
  { 
    heading: 'Scalable Architecture', 
    body: 'We engineer infrastructures designed to scale. From day one, we focus on preventing technical debt so your system grows seamlessly with your user base.',
    iconName: 'FaServer',
    colorClass: 'var(--accent)',
    bgSoftClass: 'rgba(37, 99, 235, 0.08)'
  },
  { 
    heading: 'Client-Obsessed Delivery', 
    body: 'Customer satisfaction is our motto. We align completely with your product vision, treating every launch with end-to-end dedication.',
    iconName: 'FaHeart',
    colorClass: 'var(--color-purple)',
    bgSoftClass: 'rgba(124, 58, 237, 0.08)'
  },
  { 
    heading: 'Production-Grade Code', 
    body: 'High-quality code is our defining principle. Our senior engineers follow industry best practices, clean design patterns, and rigorous verification.',
    iconName: 'FaCode',
    colorClass: 'var(--color-pink)',
    bgSoftClass: 'rgba(236, 72, 153, 0.08)'
  },
  { 
    heading: 'AI-Powered Affordability', 
    body: 'We build advanced, AI-integrated solutions. By leveraging cutting-edge automation, we deliver maximum value at a faster and smarter price point.',
    iconName: 'FaBrain',
    colorClass: 'var(--color-orange)',
    bgSoftClass: 'rgba(249, 115, 22, 0.08)'
  },
];

const iconMap = {
  FaServer: FaServer,
  FaHeart: FaHeart,
  FaCode: FaCode,
  FaBrain: FaBrain
};

const techStack = ['React', 'Node.js', 'MongoDB', 'Express', 'TypeScript', 'Next.js', 'Android', 'iOS', 'AWS', 'Docker'];

const tickerText = ['FAST DELIVERY', 'CLEAN CODE', 'SCALABLE ARCHITECTURE', 'PIXEL-PERFECT DESIGN', '24/7 SUPPORT', 'AI-POWERED SOLUTIONS', 'ZERO DOWNTIME', 'AGILE DEVELOPMENT', 'END-TO-END OWNERSHIP'];

const testimonials = [
  { 
    quote: "Bombay Coding Company made our email setup smooth and hassle-free. Their team is reliable, quick to respond, and always ready to help. No matter how small or simple our questions were, they patiently guided us and solved every issue.\n\nWhat we appreciated the most was that they even helped us with digital solutions beyond our agreement. It's great to have a team you can trust whenever you need support. We highly recommend Bombay Coding Company.", 
    author: "Raviraj Shetty", 
    role: "Director, Brihat Build Tech Pvt Ltd",
    image: RavirajPhoto
  }
];

/* ── Intersection Observer hook ─────────────────────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ── Rotating word component ─────────────────────────────────── */
const rotatingWords = [
  { text: 'Web Platforms.', color: 'var(--accent)' },
  { text: 'Native Mobile Apps.', color: 'var(--color-purple)' },
  { lines: ['AI', 'Products.'], color: 'var(--color-pink)' },
  { text: 'Digital Experiences.', color: 'var(--color-orange)' },
  { lines: ['SaaS', 'Platforms.'], color: 'var(--color-brand-deep)' },
];

function RotatingWord() {
  const [index, setIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setIndex(i => (i + 1) % rotatingWords.length);
        setAnimating(false);
      }, 350);
    }, 2400);
    return () => clearInterval(interval);
  }, []);

  const word = rotatingWords[index];
  const sharedStyle = {
    color: word.color,
    opacity: animating ? 0 : 1,
    transform: animating ? 'translateY(-16px)' : 'translateY(0)',
    filter: animating ? 'blur(6px)' : 'blur(0)',
    transition: 'opacity 0.32s ease, transform 0.32s ease, filter 0.32s ease',
  };
  if (word.lines) {
    return (
      <span className="rotating-word" style={{ ...sharedStyle, display: 'block' }}>
        {word.lines.map((line, i) => (
          <span key={i} style={{ display: 'block', lineHeight: 'inherit' }}>{line}</span>
        ))}
      </span>
    );
  }
  return (
    <span className="rotating-word" style={{ ...sharedStyle, display: 'inline-block' }}>
      {word.text}
    </span>
  );
}

/* ── Interactive Service Card Component ── */
function ServiceCard({ s, i, visible }) {
  const cardRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  return (
    <Link
      to={s.url}
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`cyber-card group relative p-8 rounded-3xl transition-all duration-500
                 hover:-translate-y-1.5 hover:shadow-2xl flex flex-col items-start gap-5
                 anim-hidden anim-delay-${Math.min(i + 1, 6)} ${visible ? 'anim-visible' : ''}`}
      style={{ 
        border: '1px solid var(--border-color)',
        background: 'var(--bg-card)',
        overflow: 'hidden',
        minHeight: '280px',
      }}
    >
      {/* Interactive spotlight glow overlay */}
      {isHovered && (
        <div
          className="pointer-events-none absolute inset-0 transition-opacity duration-300"
          style={{
            background: `radial-gradient(150px circle at ${mousePos.x}px ${mousePos.y}px, var(--accent-soft), transparent 85%)`,
            zIndex: 1,
          }}
        />
      )}

      {/* Internal cyber grid decoration */}
      <div 
        className="absolute inset-0 bg-grid-pattern opacity-[0.02] group-hover:opacity-[0.04] transition-opacity duration-500 pointer-events-none" 
        style={{ zIndex: 0 }}
      />

      {/* Hover gradient overlay */}
      <div className={`absolute inset-0 bg-gradient-to-br ${s.gradient} opacity-0
                       group-hover:opacity-[0.02] transition-opacity duration-500`} style={{ zIndex: 0 }} />

      {/* Dynamic top bar color matching category logo gradient */}
      <div 
        className={`absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r ${s.gradient}`} 
        style={{ zIndex: 2 }}
      />

      <div className={`w-14 h-14 rounded-2xl ${s.iconBg} flex items-center justify-center relative z-10
                      transition-all duration-500 group-hover:scale-110 shadow-sm`}>
        <s.icon size={26} className="opacity-90" />
      </div>

      <div className="relative z-10 flex flex-col h-full w-full">
        <h3 className="text-xl font-bold mb-2 tracking-tight group-hover:text-[var(--accent)] transition-colors duration-300"
          style={{ color: 'var(--text-primary)' }}>
          {s.title}
        </h3>
        <p className="text-xs leading-relaxed mb-4 flex-grow text-left" style={{ color: 'var(--text-secondary)' }}>
          {s.description}
        </p>

        {/* Tech tags list */}
        {s.tags && (
          <div className="flex flex-wrap gap-1.5 mb-5 pt-3 border-t w-full" style={{ borderColor: 'var(--section-divider)' }}>
            {s.tags.map((tag, idx) => (
              <span 
                key={idx} 
                className="text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-md"
                style={{ 
                  background: 'var(--bg-primary)', 
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-tertiary)' 
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-left"
          style={{ color: 'var(--accent)' }}>
          Learn More <FaArrowRight size={10} className="transform group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </Link>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */

export default function Home() {
  const [heroRef, heroVisible] = useInView(0.1);
  const [serviceRef, serviceVisible] = useInView(0.1);
  const [statsRef, statsVisible] = useInView(0.1);
  const [valuesRef, valuesVisible] = useInView(0.1);
  const [reviewsRef, reviewsVisible] = useInView(0.1);
  const [ctaRef, ctaVisible] = useInView(0.1);

  // Mouse hover state for interactive Hero Panel
  const mouseContainerRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: -1000, y: -1000 });
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseMove = (e) => {
    if (!mouseContainerRef.current) return;
    const rect = mouseContainerRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const getTiltTransform = () => {
    if (!mouseContainerRef.current || !isHovering) return 'perspective(1200px) rotateX(0deg) rotateY(0deg) scale(1)';
    const rect = mouseContainerRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateY = ((mousePos.x - centerX) / 50).toFixed(2);
    const rotateX = ((centerY - mousePos.y) / 50).toFixed(2);
    return `perspective(1200px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`;
  };

  return (
    <div className="overflow-x-hidden" style={{ background: 'var(--bg-primary)' }}>

      {/* ═══════════════════════════════════════════════════════════════
          HERO SECTION
          ═══════════════════════════════════════════════════════════════ */}
      <section
        id="hero-section"
        ref={heroRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={{
          position: 'relative',
          minHeight: '100svh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          background: 'var(--bg-primary)',
          padding: '6rem 1.5rem 4rem',
        }}
      >
        {/* ── Animated fluid orbs ─ */}
        <div className="orb spatial-orb-1" style={{
          width: 'clamp(300px, 40vw, 600px)',
          height: 'clamp(300px, 40vw, 600px)',
          top: '-10%', left: '-10%',
          background: 'var(--accent-glow)',
          opacity: 0.6,
        }} />
        <div className="orb spatial-orb-2" style={{
          width: 'clamp(250px, 35vw, 500px)',
          height: 'clamp(250px, 35vw, 500px)',
          bottom: '-5%', right: '-8%',
          background: 'var(--orb-purple)',
          opacity: 0.7,
        }} />
        <div className="orb spatial-orb-3" style={{
          width: 'clamp(180px, 25vw, 380px)',
          height: 'clamp(180px, 25vw, 380px)',
          top: '55%', left: '55%',
          background: 'rgba(236, 72, 153, 0.06)',
          opacity: 0.5,
        }} />

        {/* ── Subtle dot-grid pattern ─ */}
        <div className="absolute inset-0 bg-grid-pattern" style={{ zIndex: 0 }} />

        {/* ── B logo watermark — centered, large & subtle ─ */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none" style={{ zIndex: 1 }}>
          <div className="b-logo-wrap" style={{ width: '100vh', height: '100vh', opacity: 0.04 }}>
            <img src={BLogo} alt="" className="w-full h-full object-contain" draggable="false" />
          </div>
        </div>

        {/* ── Mouse-following radial glow ─ */}
        {isHovering && (
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              zIndex: 2,
              background: `radial-gradient(600px circle at ${mousePos.x}px ${mousePos.y}px, var(--accent-glow), transparent 70%)`,
              transition: 'background 0.08s ease',
            }}
          />
        )}

        {/* ── Main content panel (3D tilt on hover) ─ */}
        <div
          ref={mouseContainerRef}
          className={`relative z-10 text-center anim-hidden ${heroVisible ? 'anim-visible' : ''}`}
          style={{
            transform: getTiltTransform(),
            transition: isHovering ? 'transform 0.12s ease-out' : 'transform 0.6s ease',
            maxWidth: '860px',
            width: '100%',
            willChange: 'transform',
          }}
        >

          {/* Headline */}
          <h1 className="hero-massive-text" style={{ color: 'var(--text-primary)' }}>
            We Build
            <span style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              minHeight: 'clamp(8rem, 22vw, 18rem)',
              overflow: 'hidden',
            }}>
              <RotatingWord />
            </span>
          </h1>

          {/* Sub-tagline */}
          <p className="hero-tagline mx-auto" style={{ maxWidth: '560px' }}>
            A next-gen development squad building serious AI, flawless applications, and everything in between. We engineer exactly what you need to dominate the future.
          </p>

          {/* CTA Buttons */}
          <div className={`flex flex-wrap items-center justify-center gap-4 mt-10 anim-hidden anim-delay-2 ${heroVisible ? 'anim-visible' : ''}`}>
            <Link to="/contactus" className="spatial-btn-tech group" style={{ zIndex: 1 }}>
              <span className="spatial-btn-tech-bg" />
              <span className="spatial-btn-tech-shimmer" />
              <span className="relative z-10 flex items-center gap-2">
                Start a Project
                <FaArrowRight size={12} className="transform group-hover:translate-x-1.5 transition-transform duration-300" />
              </span>
            </Link>
            <Link to="/#services-section" className="spatial-btn-secondary">
              See Our Work
            </Link>
          </div>

        </div>

      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SERVICES SECTION (DYNAMIC)
          ═══════════════════════════════════════════════════════════════ */}
      <section id="services-section" ref={serviceRef}
        className="section-padding section-glow-top"
        style={{ background: 'var(--bg-surface)' }}>
        <div className="section-container">
          <div className={`text-center mb-16 anim-hidden ${serviceVisible ? 'anim-visible' : ''}`}>
            <p className="eyebrow mb-3">Capabilities</p>
            <h2 className="display-md mb-4" style={{ color: 'var(--text-primary)' }}>
              Everything You Need to <span className="gradient-text">Succeed</span>
            </h2>
            <p className="subheadline max-w-[520px] mx-auto">
              Our holistic approach integrates design, development, and strategy seamlessly.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ServicesLinks.map((s, i) => (
              <ServiceCard s={s} i={i} visible={serviceVisible} key={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          STATS SECTION
          ═══════════════════════════════════════════════════════════════ */}
      <section id="stats-section" ref={statsRef} className="py-16 section-glow-top"
        style={{
          background: 'var(--bg-card)',
          borderTop: '1px solid var(--section-divider)',
          borderBottom: '1px solid var(--section-divider)',
        }}>
        <div className="section-container px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
            {stats.map((s, i) => (
              <div
                key={i}
                className={`text-center py-8 relative
                           anim-hidden anim-delay-${i + 1} ${statsVisible ? 'anim-visible' : ''}`}
              >
                {i > 0 && (
                  <div className="hidden md:block absolute left-0 top-[20%] bottom-[20%] w-px"
                    style={{ background: 'var(--section-divider)' }} />
                )}
                <div className="display-md mb-2">
                  <span className={i % 2 === 0 ? 'gradient-text' : 'gradient-text-warm'}>{s.value}</span>
                </div>
                <p className="text-sm font-medium tracking-wide" style={{ color: 'var(--text-secondary)' }}>
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          VALUES SECTION
          ═══════════════════════════════════════════════════════════════ */}
      <section id="values-section" ref={valuesRef} className="section-padding"
        style={{ background: 'var(--bg-primary)' }}>
        <div className="section-container">
          <div className={`text-center mb-16 anim-hidden ${valuesVisible ? 'anim-visible' : ''}`}>
            <p className="eyebrow mb-3">Methodology</p>
            <h2 className="display-md" style={{ color: 'var(--text-primary)' }}>
              Powered by <span className="gradient-text-purple">Precision</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <div
                key={i}
                className={`group relative p-8 rounded-2xl transition-all duration-500
                           hover:-translate-y-1 hover:shadow-xl
                           anim-hidden anim-delay-${i + 1} ${valuesVisible ? 'anim-visible' : ''}`}
                style={{ 
                  background: 'var(--bg-card)', 
                  border: '1px solid var(--border-color)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                {/* Thin top accent glow */}
                <div 
                  className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: v.colorClass }}
                />

                <div className="relative z-10 flex flex-col items-start">
                  {/* Icon Container */}
                  <div 
                    className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 shadow-sm"
                    style={{ 
                      background: v.bgSoftClass, 
                      color: v.colorClass,
                      border: `1.5px solid ${v.bgSoftClass}`
                    }}
                  >
                    {React.createElement(iconMap[v.iconName], { size: 20 })}
                  </div>

                  <h3 className="text-lg font-bold mb-3 tracking-tight" style={{ color: 'var(--text-primary)' }}>
                    {v.heading}
                  </h3>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    {v.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          TESTIMONIALS (CLIENT REVIEWS)
          ═══════════════════════════════════════════════════════════════ */}
      <section id="reviews-section" ref={reviewsRef} className="py-24 overflow-hidden section-glow-top"
        style={{
          background: 'var(--bg-surface)',
          borderTop: '1px solid var(--section-divider)',
        }}>
        <div className="section-container mb-16">
          <div className={`text-center anim-hidden ${reviewsVisible ? 'anim-visible' : ''}`}>
            <p className="eyebrow mb-3">Client Feedback</p>
            <h2 className="display-md" style={{ color: 'var(--text-primary)' }}>
              Trusted by <span className="gradient-text">Leaders</span>
            </h2>
          </div>
        </div>

        <div className="section-container max-w-[760px] mx-auto px-4 flex justify-center">
          {testimonials.map((t, i) => (
            <div key={i} className={`cyber-card group relative overflow-hidden rounded-2xl w-full min-h-[320px] anim-hidden anim-delay-2 ${reviewsVisible ? 'anim-visible' : ''}`}
                 style={{ 
                   border: '1px solid var(--border-color)',
                   background: 'var(--bg-card)',
                   boxShadow: 'var(--shadow-card-hover)',
                 }}>
              
              <div className="flex flex-col md:flex-row">
                {/* Left section: Full half Photo Container */}
                <div className="relative w-full md:w-[42%] min-h-[220px] md:min-h-auto flex-shrink-0 overflow-hidden border-b md:border-b-0 md:border-r"
                     style={{ borderColor: 'var(--border-color)' }}>
                  <img src={t.image} alt={t.author} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  
                  {/* Fading black shadow only covering the bottom 35% of the photo space for text readability */}
                  <div className="absolute bottom-0 left-0 right-0 h-[35%] bg-gradient-to-t from-black/95 via-black/70 to-transparent" />

                  {/* Name and credentials at bottom-left corner of the image space */}
                  <div className="absolute bottom-4 left-4 right-4 z-10 text-left">
                    <h4 className="text-sm font-extrabold tracking-tight text-white">
                      {t.author}
                    </h4>
                    <p className="text-[10px] font-medium mt-0.5 text-zinc-300">
                      {t.role}
                    </p>
                    
                    {/* Tiny Verified Tag */}
                    <span className="inline-flex items-center gap-1 mt-1.5 px-2.5 py-0.5 rounded text-[8px] font-extrabold uppercase tracking-wider bg-blue-500/20 text-blue-400 border border-blue-500/30">
                      Verified
                    </span>
                  </div>
                </div>

                {/* Right section: Rating star and quote text block */}
                <div className="flex-grow p-6 md:p-8 flex flex-col justify-center items-start gap-4 text-left">
                  {/* Rating stars */}
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-4 h-4 star-glow" fill="var(--accent)" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>

                  <blockquote className="text-xs md:text-sm font-semibold italic leading-relaxed whitespace-pre-line"
                              style={{ color: 'var(--text-primary)' }}>
                    "{t.quote}"
                  </blockquote>
                </div>
              </div>

            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECOND TICKER
          ═══════════════════════════════════════════════════════════════ */}
      <div className="ticker-section py-4">
        <div className="ticker-track" style={{ animationDirection: 'reverse' }}>
          {[...tickerText, ...tickerText, ...tickerText, ...tickerText].map((t, i) => (
            <div key={i} className="ticker-item">
              <span className="gradient-text-warm font-extrabold">{t}</span>
              <div className="b-logo-wrap" style={{ width: '18px', height: '18px', opacity: 0.4, flexShrink: 0 }}>
                <img src={BLogo} alt="" className="w-full h-full object-contain" draggable="false" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          CTA SECTION
          ═══════════════════════════════════════════════════════════════ */}
      <section id="cta-section" ref={ctaRef} className="section-padding"
        style={{ background: 'var(--bg-primary)' }}>
        <div className="section-container">
          <div className={`relative rounded-3xl px-8 py-12 md:px-14 md:py-16
                           overflow-hidden border anim-hidden ${ctaVisible ? 'anim-visible' : ''}`}
            style={{ 
              background: 'var(--cta-bg)',
              borderColor: 'var(--cta-border)'
            }}>
            {/* Background glow orbs */}
            <div className="absolute top-0 left-1/4 -translate-y-1/2
                            w-[350px] h-[350px] rounded-full blur-[120px] pointer-events-none"
              style={{ background: 'var(--accent-glow)' }} />
            <div className="absolute bottom-0 right-0 translate-x-1/4 translate-y-1/4
                            w-[250px] h-[250px] rounded-full blur-[90px] pointer-events-none"
              style={{ background: 'rgba(168, 85, 247, 0.12)' }} />

            <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center justify-between">
              {/* Left content panel */}
              <div className="text-left max-w-[420px] flex-shrink-0">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] mb-3"
                  style={{ color: 'var(--accent)' }}>
                  Start Your Journey
                </p>
                <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 leading-tight text-left">
                  Let's Build Something <span className="gradient-text-warm">Great</span>
                </h2>
                <p className="text-xs md:text-sm mb-8 leading-relaxed text-left"
                  style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  Discuss your requirements with our team and get a free, no-obligation technical roadmap within 24 hours.
                </p>
                <Link to="/contactus" className="spatial-btn-tech group text-xs tracking-wider inline-flex items-center gap-1.5 !py-3 !px-6 rounded-xl font-bold relative overflow-hidden">
                  <span className="spatial-btn-tech-bg" />
                  <span className="spatial-btn-tech-shimmer" />
                  <span className="relative z-10 flex items-center gap-1.5">
                    CONTACT US NOW <FaArrowRight size={10} />
                  </span>
                </Link>
              </div>

              {/* Right content panel: TypeScript Code Window */}
              <div className="code-editor w-full md:max-w-[340px] border rounded-2xl p-4 font-mono text-[9px] md:text-[10px] leading-relaxed flex flex-col shadow-2xl overflow-hidden self-stretch md:self-auto">
                {/* Title bar buttons */}
                <div className="title-border flex items-center justify-between pb-3 border-b mb-3">
                  <div className="flex gap-1.5">
                    <span className="btn-close-red w-2.5 h-2.5 rounded-full" />
                    <span className="btn-close-yellow w-2.5 h-2.5 rounded-full" />
                    <span className="btn-close-green w-2.5 h-2.5 rounded-full" />
                  </div>
                  <span className="title-text text-[8px] uppercase tracking-wider font-bold font-sans">
                    bcc-config.ts
                  </span>
                </div>

                {/* Simulated IDE text */}
                <div className="text-left font-mono">
                  <p className="opacity-90">
                    <span className="kw">import</span> &#123; <span className="vn">Squad</span> &#125; <span className="kw">from</span> <span className="str">"@bcc/core"</span>;
                  </p>
                  <p className="mt-1.5 opacity-90">
                    <span className="kw">const</span> <span className="vn">project</span> = <span className="kw">await</span> <span className="vn">Squad</span>.<span className="vn">deploy</span>(&#123;
                  </p>
                  <p className="pl-4 opacity-90">
                    <span className="obj">scope</span>: <span className="str">"your-idea"</span>,
                  </p>
                  <p className="pl-4 opacity-90">
                    <span className="obj">stack</span>: [<span className="str">"nextjs"</span>, <span className="str">"ai-core"</span>, <span className="str">"grpc"</span>],
                  </p>
                  <p className="pl-4 opacity-90">
                    <span className="obj">scale</span>: <span className="str">"cloud-native"</span>,
                  </p>
                  <p className="pl-4 opacity-90">
                    <span className="obj">performance</span>: <span className="str">"flawless"</span>
                  </p>
                  <p className="opacity-90">
                    &#125;);
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}